// Import modules
import { Injectable, Inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { Observable, of, Subscription, Subject } from 'rxjs';
import { map, filter, takeUntil, throttleTime } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { StructureOrganizativeService } from './structure-organizative.service';
const jwtHelper = new JwtHelperService();
import { MsalBroadcastService, MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { EventMessage, EventType, AuthenticationResult } from '@azure/msal-browser';
import { SpinnerService } from './spinner.service';

/**
 * Service to use for authentication purposes
 */
@Injectable()
export class AuthService implements OnDestroy {

    /**
     * User organizational structure user
     */
    userOS;

    /**
     * Class name
     */
    className = 'AuthService';

    /**
     * user is logged
     */
    loggedIn = false;
    
    isFirstLogging = true;
    loginDisplay = false;
    private readonly _destroying$ = new Subject<void>();
    /**
     * Componente constructor
     * @param http 
     * @param environment 
     * @param router 
     * @param structureOrganizativeService 
     */
    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private http: HttpClient,
        @Inject("environment") private environment: any,
        private router: Router,
        private structureOrganizativeService: StructureOrganizativeService,
        private msalBroadcastService: MsalBroadcastService,
        public translateService: TranslateService,
        private msalAuthService: MsalService,
        private spinnerService: SpinnerService
    ) {}

    
    initMsal() {    
        this.spinnerService.loading(true);
        this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS), //  
            takeUntil(this._destroying$)
        )
        .pipe(throttleTime(1000))
        .subscribe((result: any) => {
            const payload = result.payload as AuthenticationResult;
            this.msalAuthService.instance.setActiveAccount(payload.account);
            this.checkoutAccount();
        });   
        
        this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
            takeUntil(this._destroying$)
        )
        .subscribe((result) => {
            this.logout();
        }); 
            
            this.msalAuthService.handleRedirectObservable().subscribe({ 
                next: (result: AuthenticationResult) => console.log('Redirect Success: ', result),
                error: (error) =>{
                    this.logout()
                    console.error('Redirect Error: ', error);
                    return;
                }
            }); 
    }
    
      ngOnDestroy(): void {
        this._destroying$.next(null);
        this._destroying$.complete();
      }

    deInitMsal() {
        this._destroying$.next(null);
        this._destroying$.complete();
    }


    checkoutAccount() {
        this.loggedIn = !!this.msalAuthService.instance.getActiveAccount();
        if (this.loggedIn) {
            this.getUserRolesAfterLogin().subscribe(res => {
                localStorage.setItem(`roles-${this.environment.web}`, res);
                sessionStorage.setItem('commentsArray', null)
                let rolesDecoded = jwtHelper.decodeToken(localStorage.getItem(`roles-${this.environment.web}`))
                localStorage.setItem(`language`, rolesDecoded.language.toLowerCase());
                this.translateService.use(localStorage.getItem(`language`));
                this.translateService.currentLang = this.getLanguage();
            })
        } else {
            this.router.navigate(['']);
        }
    }

    setConfigLogin() {
        this.getUserRolesAfterLogin().subscribe(res => {
            localStorage.setItem(`roles-${this.environment.web}`, res);
            sessionStorage.setItem('commentsArray', null)
            let rolesDecoded = jwtHelper.decodeToken(localStorage.getItem(`roles-${this.environment.web}`))
            localStorage.setItem(`language`, rolesDecoded.language.toLowerCase());
            this.translateService.use(localStorage.getItem(`language`));
        })
    }

    /**
     * Login with Azure AD
     */
    login() {
        const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
        if (isIE) {
        this.msalAuthService.loginRedirect();
        } else {
        this.msalAuthService.loginPopup();
        }
    }

    /** 
     * Logout service. Delete token and refresh token from local storage and exit to login
     */
    logout() {
        localStorage.removeItem(`roles-${this.environment.web}`);
        this.msalAuthService.logoutRedirect();
        this.userOS = undefined;
        this.router.navigate(['']);
    }

    /** 
     * Check if there is an user logged
     */
    isLogged() {
        return !!this.msalAuthService.instance.getActiveAccount();
    }



    /** 
     * Get user roles after login
     */
    getUserRolesAfterLogin(): Observable<any> {
        // const token = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsIng1dCI6IjJaUXBKM1VwYmpBWVhZR2FYRUpsOGxWMFRPSSIsImtpZCI6IjJaUXBKM1VwYmpBWVhZR2FYRUpsOGxWMFRPSSJ9.eyJhdWQiOiJhcGk6Ly81MjIzZTEzNC1hMTg1LTRiNDAtOTU1OS1hN2UxNjg5NWE0ZmEiLCJpc3MiOiJodHRwczovL3N0cy53aW5kb3dzLm5ldC81Y2M2YzY2ZC1mZmIyLTQ2OWYtOTM4NS1jZGE4NDBlNTc4MzYvIiwiaWF0IjoxNjY3NDg2MTU0LCJuYmYiOjE2Njc0ODYxNTQsImV4cCI6MTY2NzQ5MDkwMCwiYWNyIjoiMSIsImFpbyI6IkFWUUFxLzhUQUFBQXd3WFd2N2xKK041c09DOFBzMkgwbklNYmllSFlpb0pGbVpNdDdKeXlQWHdNcjVYZllieHdRZEFPVXl5OHprMGNuVGpySU5CMTRMakgwNVdKQkFHdWZhOGFHNXRNakFWeXlJUkI1Z1pJcmprPSIsImFtciI6WyJwd2QiLCJtZmEiXSwiYXBwaWQiOiI1MjIzZTEzNC1hMTg1LTRiNDAtOTU1OS1hN2UxNjg5NWE0ZmEiLCJhcHBpZGFjciI6IjAiLCJmYW1pbHlfbmFtZSI6IlDDqXJleiBSb2Ryw61ndWV6IiwiZ2l2ZW5fbmFtZSI6IlJpY2FyZG8iLCJpcGFkZHIiOiI3Ny4yNy4xOTIuMjMiLCJuYW1lIjoiUMOpcmV6IFJvZHLDrWd1ZXosIFJpY2FyZG8iLCJvaWQiOiJkOWI0MTUzYi02ZDc2LTQwMzMtYTgwMy0yYzNjNjlkY2I3OTkiLCJvbnByZW1fc2lkIjoiUy0xLTUtMjEtMjAwOTE1MDMwOC0xNTc1NDM4NDkwLTgwNjExMDEyNC04MDAzMzIiLCJyaCI6IjAuQVNZQWJjYkdYTExfbjBhVGhjMm9RT1Y0TmpUaEkxS0ZvVUJMbFZtbjRXaVZwUG9GQU5rLiIsInNjcCI6ImF1ZGl0bWFwIiwic3ViIjoiWlhjaGRrXzR1WUg2RTNlU2U4WEp1ZnNuQzk2S2xPS1FOeDlSWmV2eHRuSSIsInRpZCI6IjVjYzZjNjZkLWZmYjItNDY5Zi05Mzg1LWNkYTg0MGU1NzgzNiIsInVuaXF1ZV9uYW1lIjoiUFJSSUNBM0BtYXBmcmUubmV0IiwidXBuIjoiUFJSSUNBM0BtYXBmcmUubmV0IiwidXRpIjoiRDZISTluQ3NsMFc3M1ZDNDlVOHRBQSIsInZlciI6IjEuMCJ9.kEkNirdYYEEg95ScJfH5AgNRhzFwxksM0QxLZ8yLfz9tZ7AUriPHvlGXukBpaITzF--0zW1oHcFETgZH0q3COlXeV0oLX0BvypHxR2jj7TErWr0ttSY-uMly9VEq10oZiHYSdz8xFXBA5mbsZXIiYIPzpb5-ghrVy0BjIvQ4pN_BqNCo25eZHAvMeVPfaXMeiNZ0AFGTno-Mxhou0-lmv2cupi-iWSU4J6lPdnP_46fBrstZwCvusvn3YgjpgJaMb91MHVT2eiAcQ-64PktuyapZSqBYHsNogxk-j9obDyVyH6Gv53O4rhNmeA5P9KXwTW7cxfT28zLdC50LKAMtbg'    
        // let Authorization = `Bearer ${token}`
        // const httpOptions = {
        //     headers: new HttpHeaders({
        //       Authorization
        //     })
        //   };
        // return this.http.get(`https://apisb.mapfre.com/srv/audimap/users/${this.getNuuma()}/token`, httpOptions)
        
        return this.http.get(`${this.environment.base_URL}/user-token/${this.getNuuma()}/token`)
    }


    /** 
     * Get user roles from token
     */
    getUserRoles() {
        let user = this.getUser();
        let rolesField = _.get(user, 'customPayload.roles') ? 'customPayload.roles' : 'roles';
        return _.map(_.get(user, rolesField), (r: any) => r.name ? r.name : r);
    }

    /** 
     * Get language from token
     */
    getLanguage() {
        let user = this.getUser();
        return user?.language.toLowerCase() || localStorage.getItem('language');
    }

    /** 
     * Get token
     */
    getToken() {
        return localStorage.getItem('msal.idtoken');
    }

    getTokenRoles() {
        return localStorage.getItem(`roles-${this.environment.web}`);
    }


    /** 
     * Get user from tokenRoles
     */
    getUser() {
        try {
            return jwtHelper.decodeToken(this.getTokenRoles())
        }
        catch (err) {
            this.logout();
        }
    }

    /** 
     * Get user id from token
     */
    getUserId() {
        let user = this.getUser();
        let userField = _.get(user, 'customPayload.userId') ? 'customPayload.userId' : 'userId';
        return _.get(user, userField);
    }

    /**
     * Check if user has roles in token
     * @param rolesToCheck 
     */
    userHasRoles(rolesToCheck) {

        let userRoles = this.getUserRoles();
        return _.every(rolesToCheck, (r) => {
            return _.includes(userRoles, r);
        })
    }

    /**
     * Check if user has at least one role in token
     * @param rolesToCheck 
     */
    userHasAtleastOneRole(rolesToCheck) {
        let userRoles = this.getUserRoles();
        return _.some(rolesToCheck, (r) => {
            return _.includes(userRoles, r);
        })
    }

    /** 
     * Get user main organizational structure from token
     */
    getUserMainSO(): any[] {
        let user = this.getUser();
        let soField = _.get(user, 'customPayload.structureOrganizative') ? 'customPayload.structureOrganizative' : 'structureOrganizative';
        return [_.get(user, soField)];
    }

    /**
     * GET user all OS id
     */
    getUserAllIdSO(): any {
        let user = this.getUser();
        let soField = _.get(user, 'customPayload.structuresOrganizative') ? 'customPayload.structuresOrganizative' : 'structuresOrganizative';
        return _.get(user, soField);
    }

    /** 
     * Get all organizational structure from token
     */
    getUserAllSO() {
        let user = this.getUser();
        let soField = _.get(user, 'customPayload.structuresOrganizative') ? 'customPayload.structuresOrganizative' : 'structuresOrganizative';
        let soIds: any[] = _.get(user, soField);
        if (this.userOS) {
            return of(this.userOS);
        } else {
            return this.structureOrganizativeService.getAllSO().pipe(map((res: _.Dictionary<unknown>) => {
                let userOS = _.filter(res, os => {
                    return _.includes(soIds, _.get(os, 'idOrganizationalStructurePk'));
                });
                const isAdministrator = this.userHasAtleastOneRole(['ROL_ADMINISTRADOR']);
                this.userOS = isAdministrator ? res : userOS;
                return this.userOS;
            }));
        }
    }


    /** 
     * Get user first letter from token
     */
    getUserFirstLetters() {
        let nuuma = this.getNuuma();
        // TODO: Revisar con Raul, errores encontrados durante la migración a MAR
        let nuumaWithoutUPN: string = _.get(_.split(nuuma, '@'), '[0]');
        let firstLetter = _.get(nuumaWithoutUPN, '[0]');
        // let midIndex = Math.floor(_.size(nuumaWithoutUPN) / 2);
        // let midLetter = _.get(nuumaWithoutUPN, `[${midIndex}]`);
      let midLetter = _.get(nuumaWithoutUPN, '[1]');
        return _.upperCase(`${firstLetter}${midLetter}`);
    }

    /** 
     * Get UPN from token
     */
    getNuuma() {
        if (this.msalAuthService.instance.getActiveAccount()) {
            return this.msalAuthService.instance.getActiveAccount().username
        }
    }

    /**
     * GET EO by visibility criteria
     */

    getOSbyVisibilityCriteria() {
        let user = this.getUser();
        let soField = _.get(user, 'customPayload.structuresOrganizative') ? 'customPayload.structuresOrganizative' : 'structuresOrganizative';
        let soVisibilityField = _.get(user, 'customPayload.visibilityRules.ESTRUCTURA_ORGANIZATIVA') ? 'customPayload.visibilityRules.ESTRUCTURA_ORGANIZATIVA' : 'visibilityRules.ESTRUCTURA_ORGANIZATIVA';
        let soVisibility = _.get(user, soVisibilityField);
        let soIds: any[] = _.get(user, soField);
        if (soVisibility) {
            soIds = _.concat(soIds, soVisibility);
        }
        if (this.userOS) {
            return of(this.userOS);
        } else {
            return this.structureOrganizativeService.getAllSO().pipe(map((res: any[]) => {
                let userOS = _.filter(res, os => {
                    return _.includes(soIds, _.get(os, 'idOrganizationalStructurePk'));
                });
                const isAdministrator = this.userHasAtleastOneRole(['ROL_ADMINISTRADOR']);
                this.userOS = isAdministrator ? res : userOS;
                return this.userOS;
            }));
        }
    }

    verifyTokenOneMinute(token) {
        // Get expired time of token (in seconds) and substract a minute (60 seconds) to avoid requests that are valid on AWS and not valid on Mapfre Gateway
        let mapfrePaylod = jwtHelper.decodeToken(token);

        let expiredTime = mapfrePaylod.exp - 60

        // Get seconds from Unix Epoch
        let currentTime = Math.floor(new Date().getTime() / 1000)

        // If current time is greater than expired time, return 401
        if (expiredTime < currentTime) {

            // User or password is not correcct
            return false;
        }

        return true;
    }
}
